import axios from "axios";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("osp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// sessão expirada em área logada → volta para o login (evita telas travadas em "Carregando...")
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const path = window.location.pathname;
    // `/balcao` faltava aqui: o operador do balcão com sessão expirada ficava
    // preso na tela, sem ser mandado para o login como os outros perfis.
    const AREAS_LOGADAS = ["/admin", "/vendedor", "/balcao", "/conta"];
    if (error?.response?.status === 401 && localStorage.getItem("osp_token") &&
        AREAS_LOGADAS.some((a) => path.startsWith(a))) {
      localStorage.removeItem("osp_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ─── Listagens paginadas ──────────────────────────────────────────────────────
// Endpoints paginados respondem { data: [...], meta: {...} }; os ainda não
// migrados respondem o array cru. Os dois helpers abaixo aceitam as duas formas,
// então a migração pode ser feita um endpoint por vez sem quebrar tela nenhuma.

/** Itens de uma resposta de lista, seja ela array cru ou envelope { data }. */
export const rows = (res) => {
  const d = res?.data ?? res;
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.data) ? d.data : [];
};

/** Metadados de paginação; null quando o endpoint ainda devolve array cru. */
export const pageMeta = (res) => {
  const d = res?.data ?? res;
  return Array.isArray(d) ? null : (d?.meta ?? null);
};

/**
 * Converte entrada monetária em número, aceitando o que a pessoa realmente
 * digita no balcão.
 *
 * Existe porque o PDV tinha DUAS regras conflitantes no mesmo formulário: o
 * desconto usava `parseBRL` (vírgula decimal) e o pagamento usava `parseFloat`
 * (ponto decimal). Digitar "1000.50" no desconto virava R$ 100.050, e
 * "1.000,50" no pagamento virava R$ 1,00. O troco era pior: comparava um valor
 * lido por uma regra com outro lido pela outra.
 *
 * A regra aqui é decidir pelo ÚLTIMO separador — é ele que separa os centavos.
 * A exceção é o grupo de exatamente 3 dígitos com um separador só ("1.000",
 * "1,000"), que em qualquer das duas convenções significa milhar.
 */
export const parseMoney = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v ?? "").trim().replace(/[^\d.,-]/g, "");
  if (!s) return 0;

  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  const sepAt = Math.max(lastComma, lastDot);

  let normalized;
  if (sepAt < 0) {
    normalized = s;
  } else {
    const decimals = s.slice(sepAt + 1);
    const onlyOneSep = lastComma < 0 || lastDot < 0;
    // "1.000"/"1,000" com um separador só: milhar, não centavos.
    if (onlyOneSep && decimals.length === 3 && !/[.,]/.test(s.slice(0, sepAt))) {
      normalized = s.replace(/[.,]/g, "");
    } else {
      normalized = `${s.slice(0, sepAt).replace(/[.,]/g, "")}.${decimals}`;
    }
  }
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? 0 : n;
};

/** @deprecated Use `parseMoney`, que aceita vírgula e ponto. Mantido para não quebrar chamadas antigas. */
export const parseBRL = parseMoney;

/** Máscara de CPF/CNPJ conforme o tamanho. */
export const maskDoc = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11)
    return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1/$2").replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

/** Exporta linhas como CSV (ponto-e-vírgula, padrão Excel BR) e baixa o arquivo. */
export const downloadCSV = (filename, headers, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = "﻿" + [headers, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
};

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Erro inesperado";
}

export const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

/** Há promoção ativa? (discount_price > 0 e menor que o preço final). */
export const hasDiscount = (product) => {
  const d = Number(product?.discount_price) || 0;
  return d > 0 && d < (Number(product?.price) || 0);
};

/** Preço que o cliente paga: promoção quando válida, senão o preço final; soma o delta da variação. */
export const effectivePrice = (product, variant) => {
  const delta = Number(variant?.price_delta) || 0;
  return (hasDiscount(product) ? Number(product.discount_price) : (Number(product?.price) || 0)) + delta;
};

/** Preço final "cheio" (para mostrar riscado quando há promoção); soma o delta da variação. */
export const listPrice = (product, variant) => (Number(product?.price) || 0) + (Number(variant?.price_delta) || 0);

export const imgUrl = (u) =>
  u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

export const fileUrl = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api${path}?auth=${localStorage.getItem("osp_token")}`;

// ─── Agente de impressão local ("Ortopédica SP Impressão") ────────────────────
// Ponte HTTP local que imprime direto nas impressoras da máquina, sem passar pela
// nuvem. Porta padrão 9123 (configurável no app); token opcional em osp_print_token.
const PRINT_AGENT_URL = `http://127.0.0.1:${process.env.REACT_APP_PRINT_AGENT_PORT || 9123}`;

/** Converte um ArrayBuffer (PDF) em base64 sem estourar a pilha em arquivos grandes. */
function bufToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  return btoa(bin);
}

/**
 * Quanto o navegador espera por UM trabalho de impressão.
 *
 * Tem que ser MAIOR que o teto do próprio agente, senão o lado de cá desiste com
 * o papel ainda saindo: a tela dizia "não respondeu a tempo", o vendedor
 * reimprimia, e saía cupom em dobro. O agente se dá 60s por trabalho no Windows
 * (`PRINT_TIMEOUT_MS` em printEngine.js) mais 2,5s de intervalo antes de uma
 * eventual segunda tentativa — que só acontece em falha anterior ao spooler, e
 * essas falham rápido. 90s cobre isso com folga.
 *
 * Se aquele teto subir no agente, este número sobe junto.
 */
const PRINT_JOB_TIMEOUT_MS = 90000;

/** Erro do agente com código legível para quem chama decidir o que fazer. */
function agentError(message, code, status) {
  const err = new Error(message);
  err.code = code;
  if (status) err.status = status;
  return err;
}

async function agentReq(path, { method = "GET", body, timeout = 20000 } = {}) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeout);
  try {
    const headers = { "Content-Type": "application/json" };
    const tk = localStorage.getItem("osp_print_token");
    if (tk) headers["X-Print-Token"] = tk;
    const res = await fetch(`${PRINT_AGENT_URL}${path}`, {
      method, headers, signal: ctrl.signal, body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) {
      // O `code` do corpo é do agente (`no_printer`, `origin_denied`); o do
      // status cobre o que ele responde sem corpo. Quem chama usa isso para
      // separar "falta configurar" de "o agente nem está aberto" — a diferença
      // entre avisar o vendedor e ficar calado.
      const code = data.code || (res.status === 401 ? "token_invalid" : res.status === 403 ? "origin_denied" : "agent_error");
      throw agentError(data.error || `Falha na impressão (HTTP ${res.status})`, code, res.status);
    }
    return data;
  } catch (e) {
    if (e.name === "AbortError") throw agentError("O agente de impressão não respondeu a tempo.", "agent_timeout");
    if (e instanceof TypeError) throw agentError('Agente de impressão não encontrado. Abra o app "Ortopédica SP Impressão" nesta máquina.', "agent_offline");
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Perfis de papel do agente.
 *
 * A tela pede um PERFIL ("isto é um cupom"), nunca uma impressora ou uma medida
 * em milímetros: quem sabe o nome do equipamento e a largura da bobina é a
 * máquina do balcão, e isso muda de loja para loja. Antes o frontend mandava
 * `paper_width_mm: 80` fixo e tudo caía na mesma impressora padrão — o DANFE de
 * NF-e saía espremido em bobina e o cupom gastava uma folha A4 por venda.
 */
export const PRINT_PROFILE = {
  THERMAL: "thermal",   // bobina: cupom de venda, NFC-e, comprovante de caixa
  A4: "a4",             // folha: DANFE de NF-e, orçamento, contrato, relatório
  LABEL: "label",       // adesivo: etiqueta de código de barras / preço
};

export const printAgent = {
  url: PRINT_AGENT_URL,
  /** true se o agente local estiver rodando. */
  async available() { return Boolean(await this.health()); },
  /**
   * Resposta do /health, ou null se o agente não respondeu.
   * Traz `requiresToken`, que é como a tela sabe se deve pedir o token — /health
   * fica aberto de propósito, justamente para esse diagnóstico funcionar.
   * Traz também `profiles`, com quais tipos de papel já têm impressora.
   */
  async health() { try { return await agentReq("/health", { timeout: 2500 }); } catch { return null; } },
  /** Impressoras instaladas na máquina do usuário. */
  async listPrinters() { const d = await agentReq("/printers"); return d.printers || []; },
  /** Perfis configurados no agente desta máquina ({} se ele não respondeu). */
  async profiles() { const h = await this.health(); return (h && h.profiles) || {}; },
  /** Envia um job cru { profile?, printer?, mode, payload(base64|html), copies?, label? }. */
  print(job) { return agentReq("/print", { method: "POST", body: job, timeout: PRINT_JOB_TIMEOUT_MS }); },

  /** Cupom / comprovante em bobina térmica. */
  printReceipt(html, { copies, label, printer } = {}) {
    return this.print({ profile: PRINT_PROFILE.THERMAL, mode: "html", payload: html, copies, label, printer });
  },
  /** Etiqueta adesiva (código de barras, preço). */
  printLabel(html, { copies, label, printer } = {}) {
    return this.print({ profile: PRINT_PROFILE.LABEL, mode: "html", payload: html, copies, label, printer });
  },
  /**
   * Etiqueta em ZPL — o caminho da Zebra.
   *
   * `mode: "raw"` manda os bytes direto para a impressora, sem passar pelo
   * driver. É o que faz a Zebra funcionar no Windows, onde o driver ignora o
   * tamanho de página que o navegador pede e imprime no formato configurado
   * nele — adesivo deslocado do picote, escalado, ou em branco.
   *
   * Quem escolhe este caminho é a tela, lendo `profiles.label.zpl` do agente:
   * quem sabe o modelo da impressora é quem a configurou naquela máquina.
   */
  printLabelRaw(zpl, { label, printer } = {}) {
    return this.print({
      profile: PRINT_PROFILE.LABEL,
      mode: "raw",
      // A ponte transporta o payload em base64 — ZPL é texto, mas o contrato do
      // /print é o mesmo para ESC/POS e para qualquer byte cru.
      payload: btoa(unescape(encodeURIComponent(zpl))),
      label,
      printer,
    });
  },
  /** Busca um PDF autenticado do backend (contrato, orçamento, DANFE) e imprime em folha. */
  async printApiPdf(apiPath, { printer, copies, profile = PRINT_PROFILE.A4 } = {}) {
    const res = await api.get(apiPath, { responseType: "arraybuffer" });
    return this.print({ profile, mode: "pdf", payload: bufToBase64(res.data), printer, copies, label: apiPath });
  },
  /**
   * Documento do backend cujo papel depende do TIPO fiscal, decidido pelo
   * Content-Type: o DANFE de NF-e vem em PDF e é documento de folha inteira; o
   * de NFC-e vem em HTML e é cupom de bobina. Uma tela só emite os dois, então
   * a escolha do papel tem que ser feita aqui — e não pelo operador.
   */
  async printApiDoc(apiPath, { printer, copies } = {}) {
    const res = await api.get(apiPath, { responseType: "arraybuffer" });
    const ct = (res.headers["content-type"] || "").toLowerCase();
    if (ct.includes("pdf")) {
      return this.print({ profile: PRINT_PROFILE.A4, mode: "pdf", payload: bufToBase64(res.data), printer, copies, label: apiPath });
    }
    const html = new TextDecoder("utf-8").decode(res.data);
    return this.printReceipt(html, { printer, copies, label: apiPath });
  },
  /**
   * Escape hatch: HTML com papel explícito, para quando a medida vem de fora do
   * perfil. Preferir printReceipt/printLabel/printApiPdf — eles dizem a INTENÇÃO,
   * e é a intenção que sobrevive a uma loja trocar de impressora.
   */
  printHtml(html, { printer, profile, paper_width_mm, copies, label } = {}) {
    return this.print({ profile, mode: "html", payload: html, paper_width_mm, printer, copies, label });
  },
};

export default api;
